import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { compactParams, formatTat, LIVE_REFRESH_MS, saveDownload } from "@/lib/reporting";
import { Page, StatusBadge, inputCls } from "@/components/lims";
import { ClipboardText, DownloadSimple, FilePdf, TestTube, Timer } from "@phosphor-icons/react";

const emptyFilters = { status: "", date_from: "", date_to: "" };

export default function Portal() {
  const [filters, setFilters] = useState(emptyFilters);
  const [overview, setOverview] = useState(null);
  const [samples, setSamples] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const params = compactParams(filters);
      const [summary, list] = await Promise.all([
        api.get("/portal/overview", { params }),
        api.get("/portal/samples", { params }),
      ]);
      setOverview(summary.data);
      setSamples(list.data);
      setError("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  }, [filters]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, LIVE_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);
  useEffect(() => {
    // Opening the portal acknowledges released-result and order-decision
    // notifications (issue #94).
    api.post("/notifications/read").catch(() => {});
  }, []);

  const download = async (sample) => {
    try {
      const { data } = await api.get(`/portal/reports/${sample.id}.pdf`, { responseType: "blob" });
      saveDownload(data, `results-${sample.sample_code}.pdf`, "application/pdf");
      toast.success("Released report downloaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const set = (key) => (event) => setFilters({ ...filters, [key]: event.target.value });
  const brand = overview?.branding || { label: "Client Portal", color: "#0F172A" };
  const tat = overview?.tat?.overall || {};

  return (
    <Page>
      <div className="rounded-xl p-6 text-white mb-5" style={{ backgroundColor: brand.color }}>
        <div className="text-xs uppercase tracking-[0.2em] opacity-70">Secure client portal</div>
        <h1 className="mt-2 text-3xl font-display font-semibold">{brand.label}</h1>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm opacity-75">Live sample status and QA-released reports</p>
          <Link to="/portal/orders" className="inline-flex items-center gap-2 rounded-md bg-white/15 px-3 py-2 text-sm font-medium hover:bg-white/25"><ClipboardText size={17} /> Manage test requests</Link>
        </div>
      </div>

      {error && <div className="mb-4 rounded-md border border-status-critical/30 bg-status-critical/10 p-3 text-sm text-status-critical">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <div className="rounded-lg bg-white border border-slate-200 p-5"><TestTube size={21} className="text-slate-400" /><div className="text-3xl font-semibold mt-3">{overview?.total_samples ?? "…"}</div><div className="text-xs uppercase text-slate-400 mt-1">Samples</div></div>
        <div className="rounded-lg bg-white border border-slate-200 p-5"><Timer size={21} className="text-slate-400" /><div className="text-3xl font-semibold mt-3">{formatTat(tat.average_tat_days)}</div><div className="text-xs uppercase text-slate-400 mt-1">Average TAT</div></div>
        <div className="rounded-lg bg-white border border-slate-200 p-5"><FilePdf size={21} className="text-slate-400" /><div className="text-3xl font-semibold mt-3">{samples.filter((sample) => sample.has_released_report).length}</div><div className="text-xs uppercase text-slate-400 mt-1">Released reports</div></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <select className={inputCls} value={filters.status} onChange={set("status")} aria-label="Status filter">
          <option value="">All statuses</option>
          {(overview?.sample_status || []).map((row) => <option key={row.name}>{row.name}</option>)}
        </select>
        <input className={inputCls} type="date" value={filters.date_from} onChange={set("date_from")} aria-label="Received from" />
        <input className={inputCls} type="date" value={filters.date_to} onChange={set("date_to")} aria-label="Received to" />
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-auto">
        <div className="px-5 py-4 border-b border-slate-100 flex justify-between">
          <div><h2 className="font-display font-medium">Your samples</h2><p className="text-xs text-slate-400">Automatically refreshed every 15 seconds</p></div>
          <span className="text-xs text-slate-400">
            Updated{" "}
            {overview?.generated_at ? (
              <time dateTime={overview.generated_at} data-timestamp>
                {new Date(overview.generated_at).toLocaleTimeString()}
              </time>
            ) : "—"}
          </span>
        </div>
        <table className="w-full text-sm" data-testid="portal-sample-list">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="text-left px-5 py-3">Sample</th><th className="text-left px-5 py-3">Type</th><th className="text-left px-5 py-3">Received</th><th className="text-left px-5 py-3">Status</th><th className="text-right px-5 py-3">Released report</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {samples.map((sample) => {
              const receivedAt = sample.received_at || sample.created_at;
              return (
                <tr key={sample.id}>
                  <td className="px-5 py-3"><Link className="font-mono text-xs text-blue-700 hover:underline" to={`/portal/samples/${sample.id}`}>{sample.sample_code}</Link></td>
                  <td className="px-5 py-3">{sample.sample_type}</td>
                  <td className="px-5 py-3 text-slate-500">
                    {receivedAt ? (
                      <time dateTime={receivedAt} data-timestamp>{receivedAt.slice(0, 10)}</time>
                    ) : "—"}
                  </td>
                  <td className="px-5 py-3"><StatusBadge status={sample.status} /></td>
                  <td className="px-5 py-3 text-right">{sample.has_released_report ? <button onClick={() => download(sample)} className="inline-flex items-center gap-1 text-blue-700 hover:underline"><DownloadSimple size={16} /> PDF</button> : <span className="text-slate-300">Not released</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Page>
  );
}
